import { useState, type FormEvent } from 'react';
import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Eye, Plus, TriangleAlert, Unlink } from 'lucide-react';
import type { ClientUser, ClientUserCreateBody, ClientUserKeycloakCreateBody } from '@erp/shared';
import { api, apiList, type ApiError } from '@/lib/api-client';
import { isKeycloakMode } from '@/config/env';
import { Button } from '@/components/ui/button';
import { Input, Label } from '@/components/ui/input';
import { Dialog, EmptyState, Skeleton } from '@/components/ui/misc';
import { errorMessage } from '@/features/clients/clients-queries';
import { RowIconButton } from '@/features/clients/client-addresses-section';
import { useAuthUser } from '@/auth/use-auth';
import { lastSeenByUser, lastSeenText, memberAccessQuery } from '@/features/settings/member-access';
import { LOGIN_ACCOUNT_ERRORS } from '@/features/settings/login-account';
import { usePermissions } from '@/auth/use-permissions';
import { useTerms } from '@/shared/tenant-profile';
import {
  ImpersonateDialog,
  type ImpersonationTargetRef,
} from '@/features/impersonation/ImpersonateDialog';

function clientUsersQuery(clientId: number) {
  return queryOptions({
    queryKey: ['erp', 'clients', 'users', { clientId }] as const,
    queryFn: async ({ signal }) => {
      const res = await apiList<ClientUser>(`/clients/${clientId}/users`, { signal });
      if (res.error) throw res.error;
      return res.data.items;
    },
  });
}

/**
 * O e-mail já pertence a um login que não pode virar login de portal DESTA
 * empresa — o 409 do use-case: um usuário interno (staff), o login de portal de
 * outra empresa (F-108), ou a conta do SSO que o papel de portal mudaria noutra
 * empresa (F-174). A resposta não diz qual, de propósito.
 */
function isTakenEmail(err: unknown): boolean {
  return (err as ApiError | null)?.error === 'email_already_in_use';
}

function friendlyError(err: unknown): string {
  if (isTakenEmail(err)) {
    return 'Este e-mail já é de um login que não pode ser usado no portal desta empresa (da equipe, ou de outra empresa) — use outro e-mail.';
  }
  const code = (err as ApiError | null)?.error;
  if (typeof code === 'string' && LOGIN_ACCOUNT_ERRORS[code]) return LOGIN_ACCOUNT_ERRORS[code];
  return errorMessage(err);
}

/**
 * Seção "Usuários do portal" na página do cliente: lista (nome, e-mail),
 * criação (nome, e-mail, senha — ou vínculo de user client existente pelo
 * e-mail) e desvínculo (rotas /clients/:id/users).
 */
export function ClientUsersSection({ clientId }: { clientId: number }) {
  const queryClient = useQueryClient();
  const usersQ = useQuery(clientUsersQuery(clientId));
  const [createOpen, setCreateOpen] = useState(false);
  // spec 017 (US4): quem entrou — leitura de admin na API; os demais não veem a coluna
  const isAdmin = useAuthUser()?.role === 'admin';
  const accessQ = useQuery(memberAccessQuery(isAdmin));
  const access = lastSeenByUser(accessQ.data);
  // spec 020: ver o portal como esta pessoa vê (só com users.impersonate)
  const { can } = usePermissions();
  const canImpersonate = can('users.impersonate');
  const terms = useTerms();
  const [impersonating, setImpersonating] = useState<ImpersonationTargetRef | null>(null);

  const unlink = useMutation({
    mutationFn: async (userId: number) => {
      const res = await api<void>(`/clients/${clientId}/users/${userId}`, { method: 'DELETE' });
      if (res.error) throw res.error;
    },
    onSuccess: () =>
      void queryClient.invalidateQueries({ queryKey: clientUsersQuery(clientId).queryKey }),
  });

  return (
    <section className="overflow-hidden rounded-lg border bg-card shadow-xs">
      <div className="flex items-center justify-between gap-2 p-4 pb-3">
        <div>
          <h2 className="text-base font-semibold">Usuários do portal</h2>
          <p className="mt-0.5 text-[13px] text-muted-foreground">
            Pessoas deste cliente com acesso ao portal para abrir e acompanhar tickets.
          </p>
        </div>
        <Button size="sm" onClick={() => setCreateOpen(true)}>
          <Plus className="size-4" strokeWidth={2.5} />
          Usuário
        </Button>
      </div>
      {usersQ.isPending ? (
        <div className="space-y-2 px-4 pb-4" aria-busy>
          {Array.from({ length: 2 }).map((_, i) => (
            <Skeleton key={i} className="h-9 w-full" />
          ))}
        </div>
      ) : usersQ.isError ? (
        <EmptyState
          className="h-28"
          message={`Erro ao carregar usuários: ${errorMessage(usersQ.error)}`}
        />
      ) : (usersQ.data ?? []).length === 0 ? (
        <EmptyState className="h-28" message="Nenhum usuário do portal vinculado" />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted text-left text-[13px]">
                <th className="h-9 px-4 font-semibold">Nome</th>
                <th className="h-9 px-4 font-semibold">E-mail</th>
                {isAdmin ? <th className="h-9 w-40 px-4 font-semibold">Último acesso</th> : null}
                <th className="h-9 w-24 px-4">
                  <span className="sr-only">Ações</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {(usersQ.data ?? []).map((u) => (
                <tr
                  key={u.userId}
                  className="h-10 border-b border-divider transition-colors last:border-0 hover:bg-accent/60"
                >
                  <td className="max-w-52 truncate px-4 font-medium">{u.name}</td>
                  <td className="max-w-64 truncate px-4 text-muted-foreground">{u.email}</td>
                  {isAdmin ? (
                    <td className="px-4 whitespace-nowrap text-[13px] text-muted-foreground">
                      {lastSeenText(access, u.userId)}
                    </td>
                  ) : null}
                  <td className="px-4">
                    <div className="flex justify-end gap-1">
                      {canImpersonate ? (
                        <RowIconButton
                          label={`Ver como ${terms.client}: ${u.name}`}
                          onClick={() => setImpersonating({ id: u.userId, name: u.name, email: u.email })}
                        >
                          <Eye className="size-3.5" />
                        </RowIconButton>
                      ) : null}
                      <RowIconButton
                        label="Desvincular usuário"
                        destructive
                        disabled={unlink.isPending}
                        onClick={() => {
                          if (
                            window.confirm(
                              `Desvincular "${u.name}" deste cliente? Sem vínculos, o usuário perde o acesso ao portal.`
                            )
                          )
                            unlink.mutate(u.userId);
                        }}
                      >
                        <Unlink className="size-3.5" />
                      </RowIconButton>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {unlink.error ? (
        <p className="px-4 pb-3 text-sm text-destructive">{errorMessage(unlink.error)}</p>
      ) : null}
      {createOpen ? (
        <CreateClientUserDialog clientId={clientId} onClose={() => setCreateOpen(false)} />
      ) : null}
      {impersonating ? (
        <ImpersonateDialog target={impersonating} onClose={() => setImpersonating(null)} />
      ) : null}
    </section>
  );
}

function CreateClientUserDialog({
  clientId,
  onClose,
}: {
  clientId: number;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const create = useMutation({
    mutationFn: async (body: ClientUserCreateBody | ClientUserKeycloakCreateBody) => {
      const res = await api<ClientUser>(`/clients/${clientId}/users`, { method: 'POST', body });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: clientUsersQuery(clientId).queryKey });
      onClose();
    },
  });

  // Em SSO a senha não é nossa: o corpo NÃO leva password (a API recusa, 422).
  const valid =
    name.trim() !== '' && email.trim() !== '' && (isKeycloakMode || password.length >= 8);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!valid) return;
    const base = { name: name.trim(), email: email.trim() };
    create.mutate(isKeycloakMode ? base : { ...base, password });
  };

  return (
    <Dialog open onClose={onClose} title="Novo usuário do portal">
      <form onSubmit={submit} className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="client-user-name">
            Nome <span className="text-destructive">*</span>
          </Label>
          <Input
            id="client-user-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            autoFocus
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="client-user-email">
            E-mail <span className="text-destructive">*</span>
          </Label>
          <Input
            id="client-user-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <p className="text-xs text-muted-foreground">
            Se já existir um usuário do portal com este e-mail, ele será vinculado a este cliente
            (nome e senha são ignorados).
          </p>
        </div>
        {isKeycloakMode ? (
          <div className="flex items-start gap-2 rounded-md border border-[#FFE58F] bg-[#FFFBE6] px-3 py-2 text-sm text-[#D48806]">
            <TriangleAlert className="mt-0.5 size-4 shrink-0" />
            <span>
              O login é do SSO: este cadastro cria a conta (ou usa a que já existe com este
              e-mail), com o papel de portal e na organização desta empresa, sem senha. Depois,
              gere a senha temporária em <strong>Configurações › Usuários › Redefinir senha</strong>.
            </span>
          </div>
        ) : (
          <div className="space-y-1.5">
            <Label htmlFor="client-user-password">
              Senha <span className="text-destructive">*</span>
            </Label>
            <Input
              id="client-user-password"
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={8}
            />
            <p className="text-xs text-muted-foreground">Mínimo de 8 caracteres.</p>
          </div>
        )}
        {create.error ? (
          <p className="text-sm text-destructive">{friendlyError(create.error)}</p>
        ) : null}
        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="outline" size="sm" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" size="sm" disabled={create.isPending || !valid}>
            {create.isPending ? 'Criando…' : 'Criar usuário'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
